import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Checkbox, Form, TextField } from "../../../components/forms";
import { useRequestCallbackMutation } from "../../../services/callbackService";
import ViewTestimonialsDrawer from "./ViewTestimonialsDrawer";
import CallbackTimeSelector from "./CallbackTimeSelector";
import LoadingSpinner from '../../../components/loadingSpinner/LoadingSpinner';
import "react-datepicker/dist/react-datepicker.css";
import DatePickerInput from "./DatePickerInput";
import dayjs from 'dayjs';

const RequestCallbackForm = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [callbackTime, setCallbackTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const handleFirstNameChange = (e) => setFirstName(e.target.value);
  const handleLastNameChange = (e) => setLastName(e.target.value);
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(value);
  };
  
  const fieldsValidity = !firstName || !lastName || !phoneNumber  || phoneNumber.length === 3;

  const toggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const [requestCallback] = useRequestCallbackMutation();

  const handleOnBack = () => {
    navigate(-1);
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .matches(/^[A-Za-z]+$/, "First name can only contain letters"),
    lastName: Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .matches(/^[A-Za-z]+$/, "Last name can only contain letters"),
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .test("starts-with-plus", "Country code is required (e.g., +44)", (value) =>
        /^\+/.test(value || "")
      )
      .test("valid-length", "Please enter a valid phone number", (value) =>
        /^\+[0-9]{11,13}$/.test(value || "")
      ),
    callbackDate: Yup.date().nullable(),
    callbackTime: Yup.string().nullable(),
  });

  const initialValues = {
    firstName: "",
    lastName: "",
    phoneNumber: "+44",
    callbackDate: "",
    callbackTime: "",
    consent: false,
  };

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const adminEmail = "register@treat-it.clinic";
      const payload = {
        firstName: `${values.firstName}`,
        lastName: `${values.lastName}`,
        callbackDate: selectedDate,
        callbackTime: callbackTime,
        phoneNumber: values.phoneNumber,
        consentStatus: values.consent,
        email: adminEmail,
      };

      setIsSubmitting(true);
      const response = await requestCallback({ values: payload }).unwrap();
      setIsSubmitting(false);
      !isMobile && toast.success("Success! Your request has been submitted.");
      resetForm();
      setCallbackTime("");
      setSelectedDate("");
    } catch (error) {
      setIsSubmitting(false);
      toast.error("Failed to submit your request. Please try again.");
    }
  };

  const handleDateChange = (event) => {
    setSelectedDate(dayjs(event).format('YYYY-MM-DD'));
    setCallbackTime("");
  };

  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    setIsMobile(isMobile);
  }, []);

  return (
    <div className="d-flex flex-column h-100">
      <h4 className="mb-2 fw-bold">Request a callback</h4>
      <p className="text-gray-1 mb-3">
        If you've got questions or need help booking your initial consultation,
        speak to us via call.
      </p>
      <div className="d-flex mt-4 h-100">
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          className="d-flex flex-column justify-content-between"
        >
          <div className="container px-0">
            <div className="column col-md-12 col-lg-12 col-xl-9">
              <div className="row mb-4">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label className="mb-2">
                    First name<span className="text-red-1">*</span>
                  </label>
                  <TextField
                    name="firstName"
                    onChange={handleFirstNameChange}
                    placeholder="Enter your first name"
                    autoComplete="no"
                    className="p-2 px-3 fs-6 bg-transparent border card-border rounded custom-placeholder"
                  />
                </div>
                <div className="col-md-6">
                  <label className="mb-2">
                    Last name<span className="text-red-1">*</span>
                  </label>
                  <TextField
                    name="lastName"
                    onChange={handleLastNameChange}
                    placeholder="Enter your last name"
                    autoComplete="no"
                    className="p-2 px-3 fs-6 bg-transparent border card-border  rounded custom-placeholder"
                  />
                </div>
              </div>
              <div className="row mb-4">
                <div className="col-md-12">
                  <label className="mb-2">
                    Phone number<span className="text-red-1">*</span>
                  </label>
                  <TextField
                    name="phoneNumber"
                    onChange={handlePhoneNumberChange}
                    placeholder="Enter your phone number"
                    autoComplete="no"
                    className="p-2 px-3 fs-6 bg-transparent border card-border rounded custom-placeholder"
                    onKeyDown={(e) => {
                      const inputValue = e.currentTarget.value;
                      const allowedKeys = [
                        "Backspace",
                        "Tab",
                        "ArrowLeft",
                        "ArrowRight",
                        "Delete",
                      ];
                      if (
                        (!/[0-9]/.test(e.key) && e.key !== "+" && !allowedKeys.includes(e.key)) ||
                        // (e.key === "+" && e.target.selectionStart !== 0)
                        (inputValue.length <= 3 && e.key === "Backspace")
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />

                </div>
              </div>
              <div className="row mb-5">
                <div className="col-md-6 mb-4 mb-md-0">
                  <label className="mb-2">Callback date</label>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <DatePickerInput
                      name="callbackDate"
                      onChange={handleDateChange}
                      minDate={new Date()}
                      key={isSubmitting}
                    />
                  </div>
                </div>
                <div className="col-md-6 position-relative">
                  <label className="mb-2">Callback time</label>
                  {!isMobile && (
                    <div
                      className="d-flex justify-content-between align-items-center p-2 px-3 fs-6 bg-transparent border card-border rounded custom-placeholder"
                      onClick={
                        (e) => {
                          e.stopPropagation();
                          setShowTimeSlots(!showTimeSlots)
                        }}
                    >
                      <p className="mb-0">{callbackTime || "Select time"}</p>
                      <img src={`/images/vectors/clock.svg`} alt="" style={{ width: "18px" }} className="cursor-pointer" />
                    </div>
                  )}
                  {(showTimeSlots && !isMobile) && (
                    <div
                      className="position-absolute bg-white w-100"
                      style={{ paddingRight: "24px" }}
                      onClick={
                        (e) => {
                          e.stopPropagation();
                          setShowTimeSlots(!showTimeSlots)
                        }}
                    >
                      <CallbackTimeSelector
                        selectedDate={selectedDate}
                        callbackTime={callbackTime}
                        setCallbackTime={setCallbackTime}
                        limit={4}
                      />
                    </div>
                  )}
                  {isMobile && (
                    <CallbackTimeSelector
                      selectedDate={selectedDate}
                      callbackTime={callbackTime}
                      setCallbackTime={setCallbackTime}
                    />
                  )}
                </div>
              </div>
              <div className="row mb-4">
                <div className="col-md-12">
                  <Checkbox
                    label="I consent to receiving special offers and general medical information from Treat-It via Call, SMS, WhatsApp and email. (optional)"
                    name="consent"
                    className="check-lg"
                    inputClass="rounded"
                    labelClass="ms-3"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="column fixed-footer">
            <div className="d-flex flex-column flex-lg-row gap-md-4 gap-lg-5 justify-content-between align-items-center align-items-md-end align-items-lg-center inner-footer">
              <p className="privacy-text mb-12 mb-md-0">
                By creating your account you confirm you have read and accepted
                the{" "}
                <a
                  href="https://treat-it.clinic/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 fw-semibold text-decoration-underline"
                >
                  Privacy Policy
                </a>{" "}
                and the{" "}
                <a
                  href="https://treat-it.clinic/terms-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 fw-semibold text-decoration-underline"
                >
                  Terms and Conditions.
                </a>
              </p>
              <div className="d-flex flex-column-reverse flex-md-row footer-div px-5 px-md-0 py-4 py-md-0">
                <button
                  className="d-none d-md-block text-primary-600 bg-transparent py-2 px-4 w-100 w-md-fit h-fit fw-bold border-0 rounded text-nowrap"
                  onClick={handleOnBack}
                  type="button"
                >
                  <p className="mb-0 fw-bold fs-6">Back</p>
                </button>
                <button
                  className="d-block d-md-none text-primary-600 bg-transparent pt-2 pb-0 px-4 w-100 w-md-fit h-fit fw-bold border-0 rounded text-nowrap"
                  type="button"
                  onClick={toggleModal}
                >
                  <p className="mb-0 fw-bold fs-6">
                    View testimonials
                  </p>
                </button>
                <button
                    className={`bg-primary-600 text-white py-2 px-5 w-100 w-md-fit h-fit fw-bold fs-6 border-0 rounded text-nowrap ${
                    fieldsValidity
                      ? "opacity-50 cursor-not-allowed"
                      : "opacity-100 cursor-pointer"
                  }`}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <LoadingSpinner loadingText="Submit request" /> : "Submit request"}
                </button>
              </div>
            </div>
          </div>
        </Form>
      </div>
      <ViewTestimonialsDrawer show={showModal} onClose={toggleModal} />
    </div>
  );
};

export default RequestCallbackForm;
